import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

export interface GeocodeResult {
  ok: boolean;
  zip: string;
  lat: number;
  lng: number;
  placeName: string;
  state: string;
  /** Reason a fallback was used, if any. */
  warning?: string;
}

/** Lakewood Ranch fallback (34202). */
const FALLBACK: GeocodeResult = {
  ok: false,
  zip: "34202",
  lat: 27.4015,
  lng: -82.3959,
  placeName: "Lakewood Ranch",
  state: "FL",
};

/**
 * Look up lat/lng for a US ZIP code. Cached in Supabase forever (ZIP→coords
 * don't change). Falls back to Lakewood Ranch on any error so the wizard
 * never blocks the user.
 */
export const geocodeZip = createServerFn({ method: "POST" })
  .inputValidator((input: { zip: string }) => {
    const zip = String(input?.zip ?? "").trim();
    if (!/^\d{5}$/.test(zip)) {
      throw new Error("ZIP must be 5 digits");
    }
    return { zip };
  })
  .handler(async ({ data }): Promise<GeocodeResult> => {
    const { zip } = data;

    // 1. Cache hit
    try {
      const { data: cached } = await supabaseAdmin
        .from("zip_cache")
        .select("zip, lat, lng, place, state")
        .eq("zip", zip)
        .maybeSingle();
      if (cached) {
        return {
          ok: true,
          zip: cached.zip,
          lat: cached.lat,
          lng: cached.lng,
          placeName: cached.place ?? "",
          state: cached.state ?? "",
        };
      }
    } catch {
      /* fall through to live lookup */
    }

    // 2. Live lookup via Zippopotam.us (free, no key)
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) {
        return { ...FALLBACK, zip, warning: "ZIP not found — using Lakewood Ranch." };
      }
      const json = (await res.json()) as {
        places?: Array<{
          "place name"?: string;
          state?: string;
          "state abbreviation"?: string;
          longitude?: string;
          latitude?: string;
        }>;
      };
      const place = json.places?.[0];
      if (!place || !place.latitude || !place.longitude) {
        return { ...FALLBACK, zip, warning: "ZIP not found — using Lakewood Ranch." };
      }
      const lat = parseFloat(place.latitude);
      const lng = parseFloat(place.longitude);
      const state = place["state abbreviation"] ?? place.state ?? "";
      const placeName = place["place name"] ?? "";

      // 3. Cache for next time (best-effort)
      try {
        await supabaseAdmin
          .from("zip_cache")
          .insert({ zip, lat, lng, place: placeName, state });
      } catch {
        /* noop */
      }

      return { ok: true, zip, lat, lng, placeName, state };
    } catch {
      return { ...FALLBACK, zip, warning: "Couldn't reach the ZIP service — using Lakewood Ranch." };
    }
  });
